<template>
  <div>
    <template v-if="!loading">
      <unsaved-comment-modal
        :show="showUnsavedCommentModal"
        @dismiss="cancelSave"
        @on-clear-comment-and-vote="dismissCommentAndSave"
      />
      <unsaved-tags-modal
        :show="showUnsavedTagsModal"
        @cancel="cancelSave"
        @ignore="dismissTagsAndSave"
      />
      <not-relevant-review-modal
        v-model="showNotRelevantReviewModal"
        class="flex-grow-0"
        :review="review"
        @save="confirmReviewCriteria($event)"
      />
    </template>
  </div>
</template>

<script>
import UnsavedCommentModal from '@/components/Common/UnsavedCommentModal';
import UnsavedTagsModal from '@/components/Common/UnsavedTagsModal';
import NotRelevantReviewModal from '@/components/FullTextReviewPage/NotRelevantReviewModal';
import { Review, Reference, Researcher } from '@/models';
import { InclusionAssessment } from '@/enums';

const ActionType = Object.freeze({
  RelevanceAssessment: 'RelevanceAssessment',
  InclusionAssessment: 'InclusionAssessment',
  SubmitReport: 'SubmitReport',
});

export default {
  name: 'ScreeningPageSaveModals',
  components: {
    UnsavedCommentModal,
    UnsavedTagsModal,
    NotRelevantReviewModal,
  },
  props: {
    review: {
      type: Review,
      default: null,
    },
    reference: {
      type: Reference,
      default: null,
    },
    currentResearcher: {
      type: Researcher,
      default: null,
    },
    hasUnsavedComment: {
      type: Boolean,
      default: false,
    },
    hasUnsavedTags: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['save-new-item', 'select-next-reference'],
  data: () => ({
    pendingOutcome: null,
    showUnsavedCommentModal: false,
    showUnsavedTagsModal: false,
    showNotRelevantReviewModal: false,
  }),
  computed: {
    loading() {
      return (
        this.review === null ||
        this.references === null ||
        this.currentResearcher === null
      );
    },
  },
  methods: {
    setRelevanceAssessment(assessment) {
      this.cancelSave();
      this.pendingOutcome = {
        action: ActionType.RelevanceAssessment,
        actionData: {
          reference: this.reference,
          vote: assessment,
        },
      };
      this.trySaveOutcome();
    },
    setInclusionAssessment(assessment) {
      this.cancelSave();
      this.pendingOutcome = {
        action: ActionType.InclusionAssessment,
        actionData: {
          reference: this.reference,
          outcome: assessment,
        },
      };
      this.trySaveOutcome();
    },
    submitReport() {
      this.cancelSave();
      this.pendingOutcome = {
        action: ActionType.SubmitReport,
        actionData: {
          reference: this.reference,
        },
      };
      this.trySaveOutcome();
    },
    closeModals() {
      this.showUnsavedCommentModal = false;
      this.showUnsavedTagsModal = false;
      this.showNotRelevantReviewModal = false;
    },
    cancelSave() {
      this.closeModals();
      this.pendingOutcome = null;
    },
    dismissCommentAndSave() {
      this.pendingOutcome.ignoreComments = true;
      this.showUnsavedCommentModal = false;
      this.trySaveOutcome();
    },
    dismissTagsAndSave() {
      this.pendingOutcome.ignoreTags = true;
      this.showUnsavedTagsModal = false;
      this.trySaveOutcome();
    },
    confirmReviewCriteria({ reviewCriteria, notes }) {
      const { actionData } = this.pendingOutcome;
      actionData.reviewCriteria = reviewCriteria;
      actionData.notes = notes;
      this.trySaveOutcome();
    },
    validatePendingSave() {
      const pendingOutcome = this.pendingOutcome;
      const {
        action,
        actionData,
        ignoreComments,
        ignoreTags,
      } = pendingOutcome;

      if (this.hasUnsavedComment && !ignoreComments) {
        this.showUnsavedCommentModal = true;
        return false;
      }
      if (this.hasUnsavedTags && !ignoreTags) {
        this.showUnsavedTagsModal = true;
        return false;
      }

      if (action === ActionType.InclusionAssessment) {
        const { outcome, reviewCriteria } = actionData;
        if (
          outcome === InclusionAssessment.EXCLUDE &&
          !reviewCriteria
        ) {
          this.showNotRelevantReviewModal = true;
          return false;
        }
      }
      return true;
    },
    trySaveOutcome() {
      if (!this.validatePendingSave()) {
        return;
      }

      const { actionData } = this.pendingOutcome;
      this.pendingOutcome = null;
      this.$emit('save-new-item', actionData);
      this.$emit('select-next-reference', actionData.reference);
    },
  },
};
</script>
